import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck, KeyRound, X, Save, Users as UsersIcon, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { listUsers, createUser, updateUser, deleteUser } from "../lib/api";
import { ROLES, ROLE_KEYS, STAGES, STAGE_KEYS, PERMISSIONS, PERMISSION_GROUPS, ROLE_PRESETS, ALL_PERMISSION_KEYS } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";

const blank = { username: "", full_name: "", password: "", role: "student_affairs", assigned_stages: [], permissions: ROLE_PRESETS.student_affairs };

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(blank);

  const reload = async () => {
    setLoading(true);
    try {
      setRows(await listUsers());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const openNew = () => { setForm({ ...blank, permissions: ROLE_PRESETS.student_affairs || [] }); setOpenCreate(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "", permissions: u.permissions || ROLE_PRESETS[u.role] || [] }); };

  const toggleStage = (key) => {
    setForm((f) => {
      const exists = f.assigned_stages.includes(key);
      return { ...f, assigned_stages: exists ? f.assigned_stages.filter(s => s !== key) : [...f.assigned_stages, key] };
    });
  };

  const togglePerm = (key) => {
    setForm((f) => {
      const cur = f.permissions || [];
      const exists = cur.includes(key);
      return { ...f, permissions: exists ? cur.filter(p => p !== key) : [...cur, key] };
    });
  };

  const onRoleChange = (newRole) => {
    setForm((f) => ({ ...f, role: newRole, permissions: ROLE_PRESETS[newRole] ? [...ROLE_PRESETS[newRole]] : [] }));
  };

  const selectAllInGroup = (groupKey) => {
    const keys = PERMISSIONS.filter(p => p.group === groupKey).map(p => p.key);
    setForm((f) => ({ ...f, permissions: Array.from(new Set([...(f.permissions || []), ...keys])) }));
  };
  const clearAllInGroup = (groupKey) => {
    const keys = new Set(PERMISSIONS.filter(p => p.group === groupKey).map(p => p.key));
    setForm((f) => ({ ...f, permissions: (f.permissions || []).filter(p => !keys.has(p)) }));
  };

  const onCreate = async () => {
    if (!form.username || !form.full_name || !form.password) {
      toast.error("الرجاء تعبئة جميع الحقول");
      return;
    }
    try {
      await createUser({
        username: form.username, full_name: form.full_name, password: form.password,
        role: form.role, assigned_stages: form.assigned_stages,
        permissions: form.role === "super_admin" ? ALL_PERMISSION_KEYS : (form.permissions || []),
      });
      toast.success("تم إنشاء المستخدم بنجاح");
      setOpenCreate(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إنشاء المستخدم");
    }
  };

  const onUpdate = async () => {
    const payload = {
      full_name: form.full_name, role: form.role, assigned_stages: form.assigned_stages,
      active: form.active !== false,
      permissions: form.role === "super_admin" ? ALL_PERMISSION_KEYS : (form.permissions || []),
    };
    if (form.password) payload.password = form.password;
    try {
      await updateUser(editing.id, payload);
      toast.success("تم تحديث المستخدم");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحديث");
    }
  };

  const onDelete = async () => {
    try {
      await deleteUser(deleting.id);
      toast.success("تم حذف المستخدم");
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  return (
    <div>
      <PageHeader
        title="إدارة المستخدمين"
        subtitle="إنشاء وتعديل وحذف حسابات المستخدمين وتحديد صلاحياتهم"
        icon={UsersIcon}
        actions={
          <Button onClick={openNew} className="bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-new-user">
            <Plus size={16} /> مستخدم جديد
          </Button>
        }
      />

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="users-table">
            <thead>
              <tr className="bg-teal-700 text-white">
                <th className="px-3 py-3 text-center font-cairo font-semibold w-12">#</th>
                <th className="px-3 py-3 text-right font-cairo font-semibold">اسم المستخدم</th>
                <th className="px-3 py-3 text-right font-cairo font-semibold">الاسم الكامل</th>
                <th className="px-3 py-3 text-right font-cairo font-semibold">الدور</th>
                <th className="px-3 py-3 text-right font-cairo font-semibold">المراحل المخوّلة</th>
                <th className="px-3 py-3 text-right font-cairo font-semibold w-28">الصلاحيات</th>
                <th className="px-3 py-3 text-center font-cairo font-semibold w-28">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400"><Loader2 size={20} className="inline animate-spin mx-2" /> جاري التحميل...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400">لا يوجد مستخدمون بعد</td></tr>
              ) : rows.map((u, idx) => (
                <tr key={u.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-teal-50/50">
                  <td className="px-3 py-2.5 text-center text-slate-500 tabular-nums">{idx + 1}</td>
                  <td className="px-3 py-2.5 text-slate-800 font-medium">{u.username}</td>
                  <td className="px-3 py-2.5 text-slate-800">{u.full_name}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 text-teal-700 text-xs">
                      <ShieldCheck size={12} /> {ROLES[u.role]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">
                    {u.assigned_stages?.length ? u.assigned_stages.map(s => STAGES[s]).join("، ") : "جميع المراحل"}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {u.role === "super_admin" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">جميع الصلاحيات</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">{(u.permissions?.length || 0)} / {PERMISSIONS.length}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="h-8 w-8 p-0 text-teal-700 hover:bg-teal-100" data-testid={`user-edit-${u.id}`}>
                        <Pencil size={15} />
                      </Button>
                      {u.id !== currentUser?.id && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(u)} className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-100" data-testid={`user-delete-${u.id}`}>
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle className="font-cairo">إنشاء مستخدم جديد</DialogTitle></DialogHeader>
          <UserFormFields form={form} setForm={setForm} toggleStage={toggleStage} togglePerm={togglePerm} onRoleChange={onRoleChange} selectAllInGroup={selectAllInGroup} clearAllInGroup={clearAllInGroup} showPassword required />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} className="gap-1"><X size={16} /> إلغاء</Button>
            <Button onClick={onCreate} className="bg-teal-700 hover:bg-teal-800 text-white gap-1" data-testid="user-create-submit"><Save size={16} /> حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle className="font-cairo">تعديل المستخدم</DialogTitle></DialogHeader>
          {editing && <UserFormFields form={form} setForm={setForm} toggleStage={toggleStage} togglePerm={togglePerm} onRoleChange={onRoleChange} selectAllInGroup={selectAllInGroup} clearAllInGroup={clearAllInGroup} showPassword editing />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="gap-1"><X size={16} /> إلغاء</Button>
            <Button onClick={onUpdate} className="bg-teal-700 hover:bg-teal-800 text-white gap-1" data-testid="user-update-submit"><Save size={16} /> حفظ التعديلات</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cairo">حذف المستخدم</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حقاً حذف المستخدم <strong>{deleting?.username}</strong>؟ لا يمكن التراجع.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserFormFields({ form, setForm, toggleStage, togglePerm, onRoleChange, selectAllInGroup, clearAllInGroup, showPassword, required, editing }) {
  const isSuper = form.role === "super_admin";
  const perms = form.permissions || [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>اسم المستخدم {required && <span className="text-rose-500">*</span>}</Label>
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="h-11" disabled={!!editing} data-testid="user-input-username" />
        </div>
        <div className="space-y-1.5">
          <Label>الاسم الكامل {required && <span className="text-rose-500">*</span>}</Label>
          <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-11" data-testid="user-input-fullname" />
        </div>
        {showPassword && (
          <div className="space-y-1.5 md:col-span-2">
            <Label className="flex items-center gap-1.5"><KeyRound size={13} /> كلمة المرور {required && <span className="text-rose-500">*</span>}</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" placeholder={editing ? "اتركها فارغة للإبقاء على القديمة" : "6 أحرف على الأقل"} data-testid="user-input-password" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>الدور <span className="text-rose-500">*</span></Label>
          <Select value={form.role} onValueChange={onRoleChange}>
            <SelectTrigger className="h-11" dir="rtl" data-testid="user-select-role"><SelectValue /></SelectTrigger>
            <SelectContent dir="rtl">
              {ROLE_KEYS.map(k => <SelectItem key={k} value={k}>{ROLES[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">يحدد الدور الصلاحيات الافتراضية، ويمكنك تعديلها أدناه.</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>المراحل المخوّلة <span className="text-xs text-slate-400 font-normal">(فارغ = جميع المراحل)</span></Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {STAGE_KEYS.map(k => (
            <label key={k} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${form.assigned_stages.includes(k) ? "bg-teal-50 border-teal-300" : "bg-white border-slate-200"}`}>
              <Checkbox checked={form.assigned_stages.includes(k)} onCheckedChange={() => toggleStage(k)} data-testid={`user-stage-${k}`} />
              <span className="text-sm">{STAGES[k]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Granular permissions */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-teal-700" /> الصلاحيات الممنوحة</Label>
          <span className="text-[11px] text-slate-500">{isSuper ? "كل الصلاحيات تلقائياً" : `${perms.length} / ${PERMISSIONS.length}`}</span>
        </div>
        {isSuper ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900 flex items-center gap-2">
            <Lock size={14} /> المسؤول الرئيسي يمتلك كافة الصلاحيات دائماً ولا يمكن تقييدها.
          </div>
        ) : (
          <div className="space-y-3" data-testid="permissions-grid">
            {PERMISSION_GROUPS.map((g) => {
              const groupPerms = PERMISSIONS.filter(p => p.group === g.key);
              const allChecked = groupPerms.every(p => perms.includes(p.key));
              return (
                <div key={g.key} className="rounded-xl border border-slate-200 bg-white">
                  <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between rounded-t-xl">
                    <div className="font-cairo font-bold text-sm text-teal-800">{g.label}</div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => (allChecked ? clearAllInGroup(g.key) : selectAllInGroup(g.key))} className="text-[11px] px-2 py-1 rounded-md text-teal-700 hover:bg-teal-50" data-testid={`perm-group-toggle-${g.key}`}>
                        {allChecked ? "إلغاء الكل" : "تحديد الكل"}
                      </button>
                    </div>
                  </div>
                  <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {groupPerms.map((p) => (
                      <label key={p.key} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${perms.includes(p.key) ? "bg-teal-50/70 border-teal-200" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
                        <Checkbox checked={perms.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                        <span className="text-[13px] text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
